// @flow
// $FlowFixMe
import React, { useMemo } from "react";
import { get, noop, round } from "lodash";
import type { RunnerInformation, HelpTypeEnum } from "@fdr/types/ProgramPage";
import type { NullaryFn, BinaryFn } from "@tvg/types/Functional";
import buildColor from "@fdr/static-ui/ColorPalette";
import RadialValue from "../../_atoms/RadialValue";
import HalfCircleValue from "../../_atoms/HalfCircleValue";

import {
  RunnerRatingContainer,
  RattingValue,
  RatingDescription,
  RatingOnboardingTooltip,
  RatingLabel
} from "./styled-components";

type Props = {
  runner: ?RunnerInformation,
  openHelpModal: BinaryFn<HelpTypeEnum, boolean, void>,
  useOldPercentageRating: boolean,
  hasPastRaces: boolean,
  useGraphStatusV2?: boolean,
  shouldShowFirstOnboarding: boolean,
  isAllRunnersOpen: boolean,
  onTooltipClick: NullaryFn<void>
};

const RunnerRating = (props: Props) => {
  const {
    runner,
    openHelpModal,
    useOldPercentageRating,
    hasPastRaces,
    useGraphStatusV2,
    shouldShowFirstOnboarding,
    isAllRunnersOpen,
    onTooltipClick
  } = props;

  const renderOnboardingTooltip = () => (
    <RatingOnboardingTooltip onClick={onTooltipClick}>
      Tap for more info
    </RatingOnboardingTooltip>
  );

  const renderRatingValue = (
    text: HelpTypeEnum,
    total: number,
    percentage: number | string,
    isPercentage: boolean,
    index: number,
    isNotApplicable = false,
    tooltip: boolean = false,
    label?: string
  ) => (
    <RattingValue onClick={() => openHelpModal(text, isNotApplicable)}>
      {tooltip && shouldShowFirstOnboarding && renderOnboardingTooltip()}
      {useGraphStatusV2 && label && <RatingLabel>{label}</RatingLabel>}
      {useGraphStatusV2 ? (
        <HalfCircleValue
          index={index}
          id={`${get(runner, "runners[0].runnerId")}-${text}`}
          key={`${get(runner, "runners[0].runnerId")}-${text}`}
          isPercentage={isPercentage}
          total={total}
          percent={percentage}
          strokeWidth={isNotApplicable ? 0 : 7}
          width={40}
          fontSize={28}
          barColor={buildColor(label ? "green" : "blue")}
          hasStrokeAnimation={!isAllRunnersOpen}
          hasValueAnimation={!isAllRunnersOpen}
        />
      ) : (
        <RadialValue
          index={index}
          id={`${get(runner, "runners[0].runnerId")}-${text}`}
          key={`${get(runner, "runners[0].runnerId")}-${text}`}
          isPercentage={isPercentage}
          total={total}
          percent={percentage}
          width={60}
        />
      )}
      <RatingDescription
        useGraphStatusV2={useGraphStatusV2}
        hasRatingLabel={useGraphStatusV2 && label}
      >
        {text}
      </RatingDescription>
    </RattingValue>
  );

  return useMemo(() => {
    const normalisedPowerRating = +get(
      runner,
      `runners[0].handicapping.snapshot.normalisedPowerRating`,
      0
    );
    const powerRating = +get(
      runner,
      `runners[0].handicapping.snapshot.powerRating`,
      0
    );
    const highSpeed = +get(
      runner,
      `runners[0].handicapping.speedAndClass.highSpeed`,
      0
    );
    const normalisedhighSpeed = +get(
      runner,
      `runners[0].handicapping.speedAndClass.normalisedSpeedRating`,
      0
    );

    const pwrRatingPercentage =
      useOldPercentageRating || !normalisedPowerRating
        ? powerRating
        : normalisedPowerRating;

    const highSpeedPercentage =
      useOldPercentageRating || !normalisedhighSpeed
        ? highSpeed
        : normalisedhighSpeed;

    const winPercentage = +get(
      runner,
      `runners[0].handicapping.pastResults.winPercentage`,
      0
    );
    const top3Percentage = +get(
      runner,
      `runners[0].handicapping.pastResults.top3Percentage`,
      0
    );

    /* Top/2nd/3rd labels */
    const powerRatingRanking = get(
      runner,
      `runners[0].handicapping.snapshot.powerRatingRanking`
    );
    const highSpeedRanking = get(
      runner,
      `runners[0].handicapping.speedAndClass.highSpeedRanking`
    );
    const winPercentageRanking = get(
      runner,
      `runners[0].handicapping.pastResults.winPercentageRanking`
    );
    const top3PercentageRanking = get(
      runner,
      `runners[0].handicapping.pastResults.top3PercentageRanking`
    );

    return (
      <RunnerRatingContainer
        hasPastRaces={hasPastRaces}
        useGraphStatusV2={useGraphStatusV2}
      >
        {renderRatingValue(
          "PWR RATING",
          useOldPercentageRating || !normalisedPowerRating ? 130 : 100,
          useGraphStatusV2 && pwrRatingPercentage === 0
            ? "N/A"
            : pwrRatingPercentage,
          false,
          1,
          useGraphStatusV2 && pwrRatingPercentage === 0,
          false,
          useOldPercentageRating ? "" : powerRatingRanking
        )}
        {renderRatingValue(
          "HIGH SPEED",
          useOldPercentageRating || !normalisedhighSpeed ? 130 : 100,
          useGraphStatusV2 && highSpeedPercentage === 0
            ? "N/A"
            : highSpeedPercentage,
          false,
          2,
          useGraphStatusV2 && highSpeedPercentage === 0,
          true,
          useOldPercentageRating ? "" : highSpeedRanking
        )}
        {renderRatingValue(
          "WIN %",
          100,
          round(winPercentage),
          true,
          3,
          false,
          false,
          winPercentageRanking
        )}
        {renderRatingValue(
          "TOP 3",
          100,
          round(top3Percentage),
          true,
          4,
          false,
          false,
          top3PercentageRanking
        )}
      </RunnerRatingContainer>
    );
  }, [JSON.stringify(runner), isAllRunnersOpen, shouldShowFirstOnboarding]);
};

RunnerRating.defaultProps = {
  runner: null,
  openHelpModal: noop,
  useOldPercentageRating: false,
  hasPastRaces: false,
  useGraphStatusV2: false,
  shouldShowFirstOnboarding: true,
  isAllRunnersOpen: false,
  onTooltipClick: noop
};

export default RunnerRating;
